import { DateFormatter, parseAbsolute } from "@internationalized/date"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import { getLocale } from "@/utilities/etc"

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  reservation: ReservationMini
  onConfirmCancel?: () => Promise<void>
  isPending?: boolean
}

export function CancelModal({
  isOpen,
  onOpenChange,
  reservation,
  onConfirmCancel,
  isPending,
}: Props) {
  const { t, i18n } = useTranslation("reserve")
  const locale = i18n.resolvedLanguage || getLocale()
  const timezoneLocation = reservation.locationTimezone
  const cancellationDeadline = reservation.cancellationDeadline
    ? new DateFormatter(locale, {
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: timezoneLocation,
        timeZoneName: "short",
      }).format(parseAbsolute(reservation.cancellationDeadline, timezoneLocation).toDate())
    : null

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Header isCloseDisabled={isPending}>
        <Heading level={2} slot="title">
          {t("confirmation.cancellation")}
        </Heading>
      </Modal.Header>

      <Modal.Body>
        <Text level={1}>{t("confirmation.cancelConfirmBody")}</Text>
        <Text level={2}>{t("confirmation.cancelWarning")}</Text>
        {cancellationDeadline && (
          <Text level={2}>
            {t("confirmation.cancelDeadline", { time: cancellationDeadline })}
          </Text>
        )}
      </Modal.Body>

      <Modal.Footer arrangement="horizontal">
        {(state) => (
          <>
            <Button buttonType="default" onPress={state.close}>
              {t("confirmation.back")}
            </Button>
            <Button
              buttonType="primary"
              isPending={isPending}
              onPress={async () => {
                try {
                  await onConfirmCancel?.()
                } finally {
                  state.close()
                }
              }}
            >
              {t("confirmation.confirm")}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
